/**
 * PlacesAutocompleteAddressTextFieldV2.js
 *
 * Google Places Autocomplete using the new PlaceAutocompleteElement API
 * Uses 'Places API (New)' which is enabled in Google Cloud Console for the project
 */

import React, { useRef, useEffect, useState } from "react";
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Box,
  useColorModeValue,
  useColorMode,
} from "@chakra-ui/react";
import { Global, css } from "@emotion/react";
import { Field } from "formik";

/**
 * Convert new Places API addressComponents format to legacy format
 */
const convertAddressComponents = (addressComponents = []) => {
  return addressComponents.map((component) => ({
    types: component.types,
    long_name: component.longText,
    short_name: component.shortText,
  }));
};

/**
 * Format address_components array into a dictionary keyed by type
 */
const formatAddressComponentsToDict = (address_components = []) => {
  const results = {};
  address_components.forEach((ac) => {
    const { types = [], long_name, short_name } = ac;
    types.forEach((t) => (results[t] = { long_name, short_name }));
  });
  return results;
};

/**
 * Extract Tilia address format from address components dictionary
 */
const getTiliaAddressFromPlacesDict = (d = {}) => ({
  street: `${d.street_number?.long_name || ""}${
    d.route?.short_name ? ` ${d.route?.short_name}` : ""
  }`,
  street2: d.subpremise?.long_name || "",
  city:
    d.locality?.long_name ||
    d.sublocality?.long_name ||
    d.sublocality_level_1?.long_name ||
    "",
  state: d.administrative_area_level_1?.short_name || "",
  zip: `${d.postal_code?.long_name || ""}${
    d.postal_code_suffix?.long_name ? `-${d.postal_code_suffix?.long_name}` : ""
  }`,
  country: d.country?.short_name || "",
});

/**
 * PlacesAutocompleteAddressTextFieldV2
 *
 * Google Places Autocomplete using the new PlaceAutocompleteElement API.
 * Implementation follows Google's official sample:
 * https://github.com/googlemaps/js-samples/blob/main/samples/place-autocomplete-element/index.ts
 *
 * Requirements:
 * - Google Maps JS API with `libraries=places` and `v=weekly`
 * - Places API (New) enabled in Google Cloud Console
 */
const PlacesAutocompleteAddressTextFieldV2 = ({
  name,
  label,
  onUserSelection,
  country,
  isOptional = false,
  placeholder,
  requiredFieldErrorMessage = "This field is required",
  onSelectionError,
  isDisabled,
}) => {
  const containerRef = useRef();
  const autocompleteRef = useRef();
  const { colorMode } = useColorMode();
  const [hasBlurredEmpty, setHasBlurredEmpty] = useState(false);

  // Theme-aware color values (matches Chakra Input outline variant)
  // Use semantic token which respects publisher's backgroundColor config
  // Border color matches global * selector in theme/index.js
  // Text color matches body color in global styles
  const borderColor = useColorModeValue("rgba(27, 34, 40, 0.1)", "rgba(255, 255, 255, 0.1)");
  const placeholderColor = useColorModeValue("rgba(27, 34, 40, 0.4)", "rgba(255, 255, 255, 0.4)");
  const textColor = useColorModeValue("#1B2228", "white");
  const backgroundColor = "var(--tilia-colors-background)";

  useEffect(() => {
    // Bail if API not available
    if (!window.google?.maps?.places?.PlaceAutocompleteElement) {
      console.warn("PlaceAutocompleteElement not available");
      return;
    }

    // Create the autocomplete element (per Google's sample pattern)
    const placeAutocomplete = new window.google.maps.places.PlaceAutocompleteElement();
    autocompleteRef.current = placeAutocomplete;

    // Set country restriction if provided
    if (country) {
      placeAutocomplete.includedRegionCodes = [country];
    }

    // Set optional attributes
    if (placeholder) {
      placeAutocomplete.placeholder = placeholder;
    }
    if (isDisabled) {
      placeAutocomplete.disabled = true;
    }

    // Handle place selection (per Google's API reference)
    const handlePlaceSelect = async (event) => {
      try {
        const { placePrediction } = event;
        
        if (!placePrediction) {
          // shouldn't get here...
          const errMsg = "PlacesAutocompleteAddressTextFieldV2: Missing placePrediction on gmp-select event";
          console.warn(errMsg, event);
          onSelectionError?.(errMsg); // wrapping component can just switch to manual input is we get here, which we shouldn't
          return;
        }

        // Convert prediction to Place and fetch fields
        const place = placePrediction.toPlace();
        await place.fetchFields({
          fields: ["formattedAddress", "addressComponents"],
        });

        const formatted_address = place.formattedAddress;
        const addressComponents = place.addressComponents;

        if (!formatted_address && (!addressComponents || addressComponents.length === 0)) {
          return;
        }

        // Clear required validation error on valid selection
        setHasBlurredEmpty(false);

        // Convert to legacy format for backward compatibility
        const address_components = convertAddressComponents(addressComponents);
        const address_components_dict = formatAddressComponentsToDict(address_components);
        const tilia_address = getTiliaAddressFromPlacesDict(address_components_dict);

        onUserSelection?.({
          formatted_address,
          address_components,
          address_components_dict,
          tilia_address,
        });
      } catch (err) {
        console.error("PlacesAutocompleteAddressTextFieldV2 selection error:", err);
      }
    };

    // Event name per Google's API reference: 'gmp-select'
    placeAutocomplete.addEventListener("gmp-select", handlePlaceSelect);

    // Handle blur to validate required field
    const handleBlur = () => {
      if (!isOptional) {
        const inputValue = placeAutocomplete.value || "";
        setHasBlurredEmpty(!inputValue.trim());
      }
    };
    placeAutocomplete.addEventListener("blur", handleBlur);

    // Append to DOM
    containerRef.current?.appendChild(placeAutocomplete);

    // Cleanup
    return () => {
      placeAutocomplete.removeEventListener("gmp-select", handlePlaceSelect);
      placeAutocomplete.removeEventListener("blur", handleBlur);
      placeAutocomplete.remove();
    };
  }, [isOptional]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update country restriction when prop changes
  useEffect(() => {
    if (autocompleteRef.current && country) {
      autocompleteRef.current.includedRegionCodes = [country];
    }
  }, [country]);

  // Update disabled state when prop changes
  useEffect(() => {
    if (autocompleteRef.current) {
      autocompleteRef.current.disabled = !!isDisabled;
    }
  }, [isDisabled]);

  return (
    <>
      <Global
        key={colorMode}
        styles={css`
          gmp-place-autocomplete {
            width: 100%;
            background: ${backgroundColor} !important;
            color: ${textColor} !important;
            color-scheme: ${colorMode};
            border: 1px solid ${borderColor};
            border-radius: var(--tilia-radii-md, 0.375rem);
          }

          gmp-place-autocomplete .focus-ring {
            display: none !important;
          }

          gmp-place-autocomplete::part(input) {
            height: var(--tilia-sizes-10, 40px);
            font-size: var(--tilia-fontSizes-md, 1rem);
            padding-inline-start: var(--tilia-space-4, 1rem);
            padding-inline-end: var(--tilia-space-4, 1rem);
            border-radius: var(--tilia-radii-md, 0.375rem);
            border: none !important;
            background: transparent !important;
            color: ${textColor} !important;
            outline: none !important;
            transition: all 0.2s;
          }

          gmp-place-autocomplete::part(input)::placeholder {
            color: ${placeholderColor};
          }

          gmp-place-autocomplete[disabled]::part(input) {
            opacity: 0.4;
            cursor: not-allowed;
          }

          gmp-place-autocomplete::part(predictions) {
            z-index: 10000;
          }
        `}
      />
      <Field name={name}>
        {({ meta }) => {
          const formikError = meta.touched && meta.error;
          const requiredError = hasBlurredEmpty && !isOptional;
          const isInvalid = formikError || requiredError;
          return (
            <FormControl isInvalid={isInvalid} isRequired={!isOptional}>
              {label && <FormLabel>{label}</FormLabel>}
              <Box
                ref={containerRef}
                data-testid="places-autocomplete-element-container"
                className="places-autocomplete-container"
              />
              <FormErrorMessage>
                {meta.error || (requiredError && requiredFieldErrorMessage)}
              </FormErrorMessage>
            </FormControl>
          );
        }}
      </Field>
    </>
  );
};

export default PlacesAutocompleteAddressTextFieldV2;
